// Task #1625 — assignable plain-text notes on saved ads. Shared UI used by
// the Ad Library (card badge + Notes view) and the Ads Express lightbox.
// Everything here is gated behind the granted user's `ad_notes` beta flag
// (useAdNotesEnabled); the server 403s fail-closed when the flag is off.
//
// Notes can be pinned to a spot on the ad image (click the image while
// composing) or left as overall notes. Each note snapshots the ad image it
// was written on, so notes made before an image edit are flagged
// "Earlier version" (click the badge to see that version).
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, History, Loader2, MapPin, MessageSquare, RotateCcw, Send, Trash2, User as UserIcon, ImageOff, X } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { BrandDashboardComments } from "@shared/brands-dashboard";

export type AdNoteDto = {
  id: string;
  adId: string;
  brandId: string;
  authorId: string;
  assigneeId: string | null;
  text: string;
  createdAt: string | null;
  authorName: string | null;
  assigneeName: string | null;
  // Version snapshot: the ad image the note was written on. False when the
  // ad image has changed since the note was added (legacy notes = true).
  imageUrlSnapshot?: string | null;
  onCurrentVersion?: boolean;
  // Optional pin (normalized 0..1 on the ad image). Null = overall note.
  pinX?: number | null;
  pinY?: number | null;
  // Task #1730 — resolve/reopen lifecycle. Null resolvedAt = open.
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolvedByName?: string | null;
  // Present on the brand-wide list only.
  adName?: string | null;
  adImageUrl?: string | null;
  // Task #1702 — false when the note's ad row was deleted (brand-wide list
  // only). Gates the "View in My Ads" jump action.
  adExists?: boolean;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  reviewerName?: string | null;
};

type AdNotesResponse = { adImageUrl: string | null; notes: AdNoteDto[] };

type AssigneeOption = { id: string; name: string };

// Whether the current user has the `ad_notes` grant for this brand.
export function useAdNotesEnabled(brandId: string | undefined): boolean {
  const { data } = useQuery<Record<string, boolean>>({
    queryKey: ["/api/brands", brandId, "beta-features"],
    enabled: !!brandId,
    staleTime: 60_000,
  });
  return !!data?.ad_notes;
}

// adId → note count, for tile indicators. Empty when the flag is off.
export function useAdNoteCounts(brandId: string | undefined, enabled: boolean): Record<string, number> {
  const { data } = useQuery<Record<string, number>>({
    queryKey: ["/api/brands", brandId, "ad-notes", "counts"],
    enabled: !!brandId && enabled,
  });
  return data ?? {};
}

function invalidateNoteCaches(brandId: string, adId?: string) {
  queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "ad-notes"] });
  queryClient.invalidateQueries({ predicate: query =>
    query.queryKey[0] === "brands-dashboard-detail" && query.queryKey[2] === brandId });
  queryClient.invalidateQueries({ queryKey: ["brands-dashboard"] });
  if (adId) queryClient.invalidateQueries({ queryKey: ["/api/generated-ads", adId, "notes"] });
}

function noteTimestamp(createdAt: string | null): string {
  if (!createdAt) return "";
  try {
    return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
  } catch {
    return "";
  }
}

function hasPin(note: AdNoteDto): boolean {
  return note.pinX !== null && note.pinX !== undefined && note.pinY !== null && note.pinY !== undefined;
}

function isResolved(note: AdNoteDto): boolean {
  return !!note.resolvedAt;
}

// "Resolved by X · 2 days ago" line for a resolved note.
function ResolvedLine({ note }: { note: AdNoteDto }) {
  if (!isResolved(note)) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-500"
      data-testid={`text-note-resolved-${note.id}`}
    >
      <CheckCircle2 className="h-3 w-3" />
      Resolved by <span className="font-medium">{note.resolvedByName || "Unknown"}</span>
      {note.resolvedAt ? ` ${noteTimestamp(note.resolvedAt)}` : ""}
    </span>
  );
}

// Small count pill for ad tiles. Renders nothing for zero counts.
export function AdNoteCountBadge({ count, adId }: { count: number; adId: string }) {
  if (!count) return null;
  return (
    <Badge
      variant="secondary"
      className="h-5 px-1.5 gap-1 text-[10px] font-bold tabular-nums bg-background/90 shadow"
      data-testid={`badge-note-count-${adId}`}
    >
      <MessageSquare className="h-3 w-3" />
      {count}
    </Badge>
  );
}

// "Earlier version" badge — the ad image changed after this note was written.
// When we still have the snapshot, clicking opens an inline preview of the
// version the note was on. The wrapper span stops propagation for both the
// badge click and any clicks inside the portal-rendered preview dialog, so
// the badge is safe to embed inside clickable rows (e.g. Brand Notes view).
function EarlierVersionBadge({ note }: { note: AdNoteDto }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  if (note.onCurrentVersion !== false) return null;
  const badge = (
    <Badge
      variant="outline"
      className="h-5 gap-1 px-1.5 text-[10px] font-medium text-amber-600 dark:text-amber-500 border-amber-500/40"
      title={note.imageUrlSnapshot ? undefined : "The ad image has changed since this note was added"}
      data-testid={`badge-earlier-version-${note.id}`}
    >
      <History className="h-3 w-3" />
      Earlier version
    </Badge>
  );
  if (!note.imageUrlSnapshot) return badge;
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <span
        role="button"
        tabIndex={0}
        className="cursor-pointer inline-flex"
        title="The ad image has changed since this note was added — click to preview the version it was written on"
        onClick={(e) => {
          e.preventDefault();
          setPreviewOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(true);
          }
        }}
        data-testid={`link-earlier-version-${note.id}`}
      >
        {badge}
      </span>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-xl" data-testid={`dialog-earlier-version-${note.id}`}>
          <DialogTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            Image this note was written on
          </DialogTitle>
          <img
            src={note.imageUrlSnapshot}
            alt="Earlier version of the ad"
            className="mx-auto block max-h-[70vh] max-w-full w-auto rounded-md border"
            data-testid={`img-earlier-version-${note.id}`}
          />
          <p className="text-xs text-muted-foreground">
            The ad image has changed since this note was added — this is the version the note
            {hasPin(note) ? " and its pin were" : " was"} written on.
          </p>
        </DialogContent>
      </Dialog>
    </span>
  );
}

function NoteRow({
  note,
  pinNumber,
  active,
  onActivate,
  canDelete,
  onDelete,
  deleting,
  onToggleResolved,
  resolving,
}: {
  note: AdNoteDto;
  pinNumber: number | null;
  active: boolean;
  onActivate?: () => void;
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
  // Task #1730 — resolve/reopen (any brand member).
  onToggleResolved: () => void;
  resolving: boolean;
}) {
  const resolved = isResolved(note);
  return (
    <div
      className={`rounded-md border p-2.5 space-y-1 ${active ? "border-primary ring-1 ring-primary" : "border-border/60"} ${resolved ? "opacity-60" : ""}`}
      onClick={onActivate}
      data-testid={`note-${note.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap min-w-0">
          {pinNumber !== null && (
            <span
              className="inline-flex items-center gap-0.5 font-bold text-primary"
              title="Pinned to a spot on the ad"
              data-testid={`badge-note-pin-${note.id}`}
            >
              <MapPin className="h-3 w-3" />
              {pinNumber}
            </span>
          )}
          <span className="font-medium text-foreground">{note.authorName || "Unknown"}</span>
          {note.assigneeId && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" />
              for <span className="font-medium text-foreground">{note.assigneeName || "Unknown"}</span>
            </span>
          )}
          <span>· {noteTimestamp(note.createdAt)}</span>
          <EarlierVersionBadge note={note} />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-1.5 text-[11px] ${resolved ? "text-muted-foreground" : "text-emerald-600 dark:text-emerald-500 hover:text-emerald-700"}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleResolved();
            }}
            disabled={resolving}
            data-testid={`button-${resolved ? "reopen" : "resolve"}-note-${note.id}`}
          >
            {resolving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : resolved ? (
              <><RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen</>
            ) : (
              <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Resolve</>
            )}
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={deleting}
              data-testid={`button-delete-note-${note.id}`}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          )}
        </div>
      </div>
      <p className={`text-sm whitespace-pre-wrap break-words ${resolved ? "text-muted-foreground" : ""}`} data-testid={`text-note-${note.id}`}>{note.text}</p>
      {resolved && (
        <p className="text-[11px]">
          <ResolvedLine note={note} />
        </p>
      )}
    </div>
  );
}

// Numbered pin marker on the ad image. Stale = the note was written on an
// earlier version of the image, so the position may no longer line up.
function PinMarker({
  x,
  y,
  label,
  active,
  stale,
  onClick,
  testId,
}: {
  x: number;
  y: number;
  label: string;
  active?: boolean;
  stale?: boolean;
  onClick?: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={`absolute z-10 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[10px] font-bold shadow-md border border-white/80 transition-transform ${
        stale ? "bg-amber-500 text-white" : "bg-primary text-primary-foreground"
      } ${active ? "scale-125 ring-2 ring-primary/50" : "hover:scale-110"}`}
      style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
      title={stale ? "Pinned on an earlier version of this ad — the spot may have moved" : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

// Finds an in-progress "@mention" ending at the caret: the last "@" that is
// at the start of the text or preceded by whitespace, with no other "@" or
// newline between it and the caret. Returns null when the caret isn't inside
// a mention being typed.
function activeMentionAt(text: string, caret: number): { start: number; query: string } | null {
  const upToCaret = text.slice(0, caret);
  const at = upToCaret.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(upToCaret[at - 1])) return null;
  const query = upToCaret.slice(at + 1);
  if (/[\n@]/.test(query)) return null;
  return { start: at, query };
}

// Notes list + add form for one saved ad. Assignment is done by typing "@"
// in the note text and picking a person from the suggestion list — no
// dropdown (user preference). When the ad image is shown, clicking it pins
// the note being composed to that spot (optional — unpinned notes are
// overall notes).
export function AdNotesPanel({ adId, brandId }: { adId: string; brandId: string }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [text, setText] = useState("");
  const [assignee, setAssignee] = useState<AssigneeOption | null>(null);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingPin, setPendingPin] = useState<{ x: number; y: number } | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const noteRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const { data, isLoading } = useQuery<AdNotesResponse>({
    queryKey: ["/api/generated-ads", adId, "notes"],
  });
  const notes = data?.notes ?? [];
  const adImageUrl = data?.adImageUrl ?? null;

  const { data: assignees = [] } = useQuery<AssigneeOption[]>({
    queryKey: ["/api/brands", brandId, "ad-notes", "assignees"],
  });

  // Pinned notes get stable numbers in list order (1, 2, …) shared between
  // the markers on the image and the badges on the rows.
  const pinNumberById = useMemo(() => {
    const map = new Map<string, number>();
    let n = 0;
    for (const note of notes) {
      if (hasPin(note)) map.set(note.id, ++n);
    }
    return map;
  }, [notes]);

  useEffect(() => {
    if (!activeNoteId) return;
    const el = noteRefs.current.get(activeNoteId);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeNoteId]);

  const suggestions = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.trim().toLowerCase();
    const pool = q
      ? assignees.filter((a) => a.name.toLowerCase().includes(q))
      : assignees;
    return pool.slice(0, 6);
  }, [mention, assignees]);

  const syncMention = (value: string, caret: number) => {
    const m = activeMentionAt(value, caret);
    setMention(m);
    if (m) setHighlightIdx(0);
  };

  const pickAssignee = (option: AssigneeOption) => {
    if (!mention) return;
    const caret = textareaRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, mention.start);
    const after = text.slice(caret);
    const inserted = `@${option.name} `;
    const next = `${before}${inserted}${after}`;
    setText(next);
    setAssignee(option);
    setMention(null);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const pos = before.length + inserted.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const placePin = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setPendingPin({ x, y });
    textareaRef.current?.focus();
  };

  const addNote = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/generated-ads/${adId}/notes`, {
        text: text.trim(),
        assigneeId: assignee?.id ?? null,
        pinX: pendingPin?.x ?? null,
        pinY: pendingPin?.y ?? null,
      });
      return res.json();
    },
    onSuccess: () => {
      setText("");
      setAssignee(null);
      setMention(null);
      setPendingPin(null);
      invalidateNoteCaches(brandId, adId);
    },
    onError: (e: any) => {
      toast({ title: "Couldn't add note", description: e?.message, variant: "destructive" });
    },
  });

  const deleteNote = useMutation({
    mutationFn: async (noteId: string) => apiRequest("DELETE", `/api/ad-notes/${noteId}`),
    onSuccess: () => invalidateNoteCaches(brandId, adId),
    onError: (e: any) => {
      toast({ title: "Couldn't delete note", description: e?.message, variant: "destructive" });
    },
    onSettled: () => setDeletingId(null),
  });

  // Task #1730 — resolve/reopen. Any brand member can do either.
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const toggleResolved = useMutation({
    mutationFn: async ({ noteId, resolved }: { noteId: string; resolved: boolean }) =>
      apiRequest("POST", `/api/ad-notes/${noteId}/resolution`, { resolved }),
    onSuccess: () => invalidateNoteCaches(brandId, adId),
    onError: (e: any) => {
      toast({ title: "Couldn't update note", description: e?.message, variant: "destructive" });
    },
    onSettled: () => setResolvingId(null),
  });

  const composer = (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            syncMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onClick={(e) => syncMention(text, e.currentTarget.selectionStart ?? text.length)}
          onKeyDown={(e) => {
            if (mention && suggestions.length > 0) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightIdx((i) => (i + 1) % suggestions.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                pickAssignee(suggestions[Math.min(highlightIdx, suggestions.length - 1)]);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setMention(null);
                return;
              }
            }
          }}
          onBlur={() => setTimeout(() => setMention(null), 150)}
          placeholder="Add a note… type @ to assign someone"
          rows={2}
          maxLength={2000}
          className="text-sm resize-none"
          data-testid="input-note-text"
        />
        {mention && suggestions.length > 0 && (
          <div
            className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border bg-popover shadow-md overflow-hidden"
            data-testid="list-mention-suggestions"
          >
            {suggestions.map((a, i) => (
              <button
                key={a.id}
                type="button"
                className={`w-full text-left px-2.5 py-1.5 text-sm flex items-center gap-2 ${
                  i === highlightIdx ? "bg-accent text-accent-foreground" : ""
                }`}
                onMouseEnter={() => setHighlightIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickAssignee(a);
                }}
                data-testid={`option-mention-${a.id}`}
              >
                <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {a.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {assignee ? (
          <Badge variant="secondary" className="h-7 gap-1 pl-2 pr-1 text-xs" data-testid="badge-note-assignee">
            <UserIcon className="h-3 w-3" />
            {assignee.name}
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 ml-0.5"
              onClick={() => setAssignee(null)}
              data-testid="button-clear-note-assignee"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Type @ to assign (optional)</span>
        )}
        {pendingPin && (
          <Badge variant="secondary" className="h-7 gap-1 pl-2 pr-1 text-xs" data-testid="badge-pending-pin">
            <MapPin className="h-3 w-3 text-primary" />
            Pinned to spot
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 ml-0.5"
              onClick={() => setPendingPin(null)}
              data-testid="button-clear-pending-pin"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        )}
        <Button
          size="sm"
          className="h-8 ml-auto"
          disabled={!text.trim() || addNote.isPending}
          onClick={() => addNote.mutate()}
          data-testid="button-add-note"
        >
          {addNote.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Add note</span>
        </Button>
      </div>
    </div>
  );

  const list = isLoading ? (
    <div className="flex justify-center py-4">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    </div>
  ) : notes.length === 0 ? (
    <p className="text-xs text-muted-foreground text-center py-2" data-testid="text-no-notes">
      No notes yet.
    </p>
  ) : (
    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
      {notes.map((n) => (
        <div
          key={n.id}
          ref={(el) => {
            if (el) noteRefs.current.set(n.id, el);
            else noteRefs.current.delete(n.id);
          }}
        >
          <NoteRow
            note={n}
            pinNumber={pinNumberById.get(n.id) ?? null}
            active={activeNoteId === n.id}
            onActivate={hasPin(n) ? () => setActiveNoteId(n.id) : undefined}
            canDelete={!!user && (n.authorId === user.id || !!(user as any).isMaster)}
            deleting={deletingId === n.id && deleteNote.isPending}
            onDelete={() => {
              setDeletingId(n.id);
              deleteNote.mutate(n.id);
            }}
            resolving={resolvingId === n.id && toggleResolved.isPending}
            onToggleResolved={() => {
              setResolvingId(n.id);
              toggleResolved.mutate({ noteId: n.id, resolved: !isResolved(n) });
            }}
          />
        </div>
      ))}
    </div>
  );

  if (!adImageUrl) {
    return (
      <div className="space-y-3">
        {composer}
        {list}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="sm:w-[45%] shrink-0 space-y-1.5">
        <div className="flex justify-center">
          <div
            className="relative inline-block cursor-crosshair select-none"
            onClick={placePin}
            data-testid="img-note-pin-surface"
          >
            <img
              src={adImageUrl}
              alt="Ad"
              className="block max-h-[520px] max-w-full w-auto rounded-md border"
              draggable={false}
            />
            {notes.filter(hasPin).map((n) => (
              <PinMarker
                key={n.id}
                x={n.pinX as number}
                y={n.pinY as number}
                label={String(pinNumberById.get(n.id) ?? "")}
                active={activeNoteId === n.id}
                stale={n.onCurrentVersion === false}
                onClick={() => setActiveNoteId(n.id)}
                testId={`pin-note-${n.id}`}
              />
            ))}
            {pendingPin && (
              <span
                className="absolute z-20 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/90 border-2 border-white shadow-md animate-pulse pointer-events-none"
                style={{ left: `${pendingPin.x * 100}%`, top: `${pendingPin.y * 100}%` }}
                data-testid="pin-pending"
              />
            )}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground text-center">
          {pendingPin ? "Pin placed — click again to move it" : "Click the image to pin your note to a spot (optional)"}
        </p>
      </div>
      <div className="flex-1 min-w-0 space-y-3">
        {composer}
        {list}
      </div>
    </div>
  );
}

// Modal wrapper around AdNotesPanel, used by both pages.
export function AdNotesDialog({
  open,
  onOpenChange,
  adId,
  brandId,
  adName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adId: string | null;
  brandId: string;
  adName?: string | null;
}) {
  const [, setLocation] = useLocation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="dialog-ad-notes">
        <DialogTitle className="flex min-w-0 max-w-full items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">Notes{adName ? ` — ${adName}` : ""}</span>
          {adId && (
            // Task #1702 — jump to the ad's card in the Ad Library so the
            // full action set (Edit, Reframe, Fix bands…) is at hand.
            <Button
              variant="outline"
              size="sm"
              className="ml-auto mr-6 h-7 shrink-0 text-xs"
              onClick={() => {
                onOpenChange(false);
                setLocation(`/brands/${brandId}/library?adId=${adId}`);
              }}
              data-testid="button-view-in-my-ads"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              View in My Ads
            </Button>
          )}
        </DialogTitle>
        {open && adId && <AdNotesPanel adId={adId} brandId={brandId} />}
      </DialogContent>
    </Dialog>
  );
}

// Brand-wide Notes view (Ad Library). Lists every note with an ad thumbnail;
// "Assigned to me" filters client-side; clicking a row jumps to the ad's
// notes via onOpenAd.
// Task #1724 — server-backed page size for the brand-wide notes list.
const BRAND_NOTES_PAGE_SIZE = 20;

export function BrandNotesView({
  brandId,
  onOpenAd,
  masterReview = false,
}: {
  brandId: string;
  onOpenAd: (note: AdNoteDto) => void;
  masterReview?: boolean;
}) {
  const { user } = useAuth();
  const canReview = masterReview && user?.isMaster === true;
  const reviewQueryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [assignedToMe, setAssignedToMe] = useState(false);
  // Task #1730 — open vs resolved filter, defaulting to open notes (also
  // when "Assigned to me" is on). Resets pagination on change.
  const [resolution, setResolution] = useState<"all" | "open" | "resolved">(
    canReview ? "all" : "open",
  );
  // Task #1724 — server-backed pagination (mirrors the admin-leads pattern).
  const [page, setPage] = useState(1);

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(BRAND_NOTES_PAGE_SIZE));
  params.set("resolution", resolution);
  if (assignedToMe) params.set("assignee", "me");

  const { data, isLoading, isError, refetch } = useQuery<{ rows: AdNoteDto[]; total: number; page: number; pageSize: number }>({
    enabled: !masterReview || canReview,
    queryKey: canReview
      ? ["brands-dashboard-detail", user?.id, brandId, params.toString()]
      : ["/api/brands", brandId, "ad-notes", params.toString()],
    queryFn: async () => {
      if (canReview) {
        const response = await apiRequest("GET", `/api/brands-dashboard/brands/${brandId}/comments?${params}`);
        const result: BrandDashboardComments = await response.json();
        return {
          ...result,
          rows: result.rows.map(note => ({
            ...note, assigneeId: note.assigneeId ?? null, assigneeName: note.assigneeName ?? null,
            resolvedByName: note.resolverName, adImageUrl: note.imageUrlSnapshot ?? null, adExists: true,
          })),
        };
      }
      const res = await fetch(`/api/brands/${brandId}/ad-notes?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });
  const [reviewError, setReviewError] = useState("");
  const review = useMutation({
    mutationFn: (commentIds: string[]) => apiRequest("POST",
      `/api/brands-dashboard/brands/${brandId}/comments/review`, { commentIds }),
    onMutate: () => setReviewError(""),
    onSuccess: async () => {
      await Promise.all([
        reviewQueryClient.invalidateQueries({ queryKey: ["brands-dashboard-detail", user?.id, brandId] }),
        reviewQueryClient.invalidateQueries({ queryKey: ["brands-dashboard", user?.id] }),
      ]);
    },
    onError: (error) => setReviewError(error instanceof Error ? error.message : "Could not mark comments reviewed."),
  });

  const visible = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / BRAND_NOTES_PAGE_SIZE));
  if (masterReview && !canReview) return <p data-testid="brands-dashboard-denied">Master access required</p>;

  return (
    <Card className="p-3 sm:p-4 space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">All notes</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-bold tabular-nums">
          {total}
        </Badge>
        {/* Task #1730 — Open / Resolved filter. */}
        <div className="flex items-center rounded-md border p-0.5">
          {(canReview ? ["all", "open", "resolved"] as const : ["open", "resolved"] as const).map((r) => (
            <Button
              key={r}
              variant={resolution === r ? "secondary" : "ghost"}
              size="sm"
              className="h-6 px-2 text-xs capitalize"
              onClick={() => { setResolution(r); setPage(1); }}
              data-testid={`button-notes-filter-${r}`}
            >
              {r === "resolved" && <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
              {r}
            </Button>
          ))}
        </div>
        <div className="ml-auto">
          <Button
            variant={assignedToMe ? "secondary" : "ghost"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => { setAssignedToMe((v) => !v); setPage(1); }}
            data-testid="button-filter-assigned-to-me"
          >
            <UserIcon className="h-3.5 w-3.5 mr-1" />
            Assigned to me
          </Button>
        </div>
      </div>
      {canReview && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">Shared master review · reading does not mark comments reviewed.</p>
          <Button size="sm" variant="outline" data-testid="button-review-displayed"
            disabled={review.isPending || isLoading || isError || !visible.some(note => !note.reviewedAt)}
            onClick={() => review.mutate(visible.filter(note => !note.reviewedAt).map(note => note.id))}>
            Mark displayed unreviewed ({visible.filter(note => !note.reviewedAt).length})
          </Button>
        </div>
      )}
      {reviewError && <div role="alert" className="rounded-md border border-destructive/30 p-3 text-sm">
        {reviewError} <Button variant="ghost" size="sm" onClick={() => setReviewError("")}>Dismiss</Button>
      </div>}
      {isError ? <div role="alert" className="py-8 text-center text-sm">
        Comments could not be loaded. <Button variant="outline" size="sm" onClick={() => void refetch()}>Retry</Button>
      </div> : isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="text-no-brand-notes">
          {resolution === "all" ? "No comments in this view." : resolution === "resolved"
            ? assignedToMe ? "No resolved notes assigned to you." : "No resolved notes yet."
            : assignedToMe ? "No open notes assigned to you." : "No open notes. Add notes from any saved ad."}
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((n) => (
            <div key={n.id} data-testid={`comment-${n.id}`}>
            <button
              type="button"
              onClick={() => onOpenAd(n)}
              className={`w-full text-left rounded-md border border-border/60 p-2.5 flex gap-3 hover-elevate ${isResolved(n) ? "opacity-60" : ""}`}
              data-testid={`row-brand-note-${n.id}`}
            >
              <div className="h-12 w-12 rounded bg-muted/60 overflow-hidden shrink-0 flex items-center justify-center">
                {n.adImageUrl ? (
                  <img src={n.adImageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageOff className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
                  <span className="font-medium text-foreground truncate max-w-[16rem]">{n.adName || "Untitled ad"}</span>
                  <span>· {n.authorName || "Unknown"}</span>
                  {n.assigneeId && (
                    <span className="inline-flex items-center gap-1">
                      <UserIcon className="h-3 w-3" />
                      {n.assigneeName || "Unknown"}
                    </span>
                  )}
                  <span>· {noteTimestamp(n.createdAt)}</span>
                  {hasPin(n) && (
                    <span className="inline-flex items-center gap-0.5 text-primary" title="Pinned to a spot on the ad">
                      <MapPin className="h-3 w-3" />
                    </span>
                  )}
                  <EarlierVersionBadge note={n} />
                  <ResolvedLine note={n} />
                </div>
                <p className={`text-sm ${canReview ? "whitespace-pre-wrap" : "line-clamp-2"} break-words ${isResolved(n) ? "text-muted-foreground" : ""}`}>{n.text}</p>
              </div>
              {/* Task #1702 — jump to the ad card in My Ads (hidden when the
                  ad row no longer exists). Rendered as a span because rows
                  are already <button>s. */}
              {n.adExists !== false && (
                <span
                  role="button"
                  tabIndex={0}
                  title="View in My Ads"
                  className="shrink-0 self-start inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocation(`/brands/${brandId}/library?adId=${n.adId}`);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setLocation(`/brands/${brandId}/library?adId=${n.adId}`);
                    }
                  }}
                  data-testid={`link-goto-ad-${n.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </span>
              )}
            </button>
            {canReview && <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-2 text-xs text-muted-foreground">
              {n.reviewedAt ? <span>Reviewed by {n.reviewerName || n.reviewedBy || "Unknown"} · {new Date(n.reviewedAt).toLocaleString()}</span>
                : <span>Needs review</span>}
              {!n.reviewedAt && <Button size="sm" variant="outline" disabled={review.isPending}
                data-testid={`button-review-${n.id}`} onClick={() => review.mutate([n.id])}>Mark reviewed</Button>}
            </div>}
            </div>
          ))}
        </div>
      )}
      {/* Task #1724 — Previous/Next paging, styled like the admin-leads pagination. */}
      {total > BRAND_NOTES_PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="text-brand-notes-pagination">
            Showing {(page - 1) * BRAND_NOTES_PAGE_SIZE + 1}–{Math.min(page * BRAND_NOTES_PAGE_SIZE, total)} of {total.toLocaleString()} notes
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="button-brand-notes-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="button-brand-notes-next"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
